import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { useCarousel } from '@/hooks/useCarousel';
import videoCard1 from '@/assets/productCenter/ai/video-card1.mp4';
import videoCard2 from '@/assets/productCenter/ai/video-card2.mp4';
import videoCard3 from '@/assets/productCenter/ai/video-card3.mp4';

const CARD_COUNT = 3;
const TRACK_GAP = 40;

// 卡片与视频严格一一对应：CARD_LIST[i] 对应 video-card(i+1).mp4，请勿打乱顺序
const CARD_LIST = [
  {
    title: '因材施教，学有所成',
    desc: '过程检测掌握情况，随时调整学习难度，将难度与学习方法精准结合，支撑多元学习场景。',
    video: videoCard3,
  },
  {
    title: '注意力集中，全程不走神',
    desc: '短时高频互动，调配学习者注意力，将专注力与思考力从“分开动”到“一起动”。',
    video: videoCard1,
  },

  {
    title: '及时鼓励，激发动力',
    desc: '创造及时互动反馈，让“理解我，鼓励我”成为学习自信的根本来源，激发学习潜能，强化学习动力。',
    video: videoCard2,
  },
];

const LARGE_BASE_W = 792;
const LARGE_BASE_H = 310;
const NORMAL_SCALE = 0.8;
const CENTER_SCALE = 1.2;
const LARGE_BREAKPOINT_HEIGHT = 800;

export default function Screen2() {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [windowWidthPx, setWindowWidthPx] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  const [windowHeightPx, setWindowHeightPx] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 900,
  );
  const [carouselWrapWidth, setCarouselWrapWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1920,
  );
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSlideChangeRef = useRef<((newIndex: number) => void) | null>(null);
  const currentIndexRef = useRef(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const { currentIndex, goToSlide, carouselRef } = useCarousel({
    totalItems: CARD_COUNT,
    initialIndex: 1,
    enableWheel: true,
    enableTouch: true,
    wheelThreshold: 3,
    switchDelay: 700,
    wrap: false,
    onWheelSlideChange: (newIndex) => handleSlideChangeRef.current?.(newIndex),
  });
  currentIndexRef.current = currentIndex;

  const isLarge = windowHeightPx > LARGE_BREAKPOINT_HEIGHT;
  const baseW = isLarge ? LARGE_BASE_W : LARGE_BASE_W * NORMAL_SCALE;
  const baseH = isLarge ? LARGE_BASE_H : LARGE_BASE_H * NORMAL_SCALE;
  const cardWidths = [0, 1, 2].map((i) => (currentIndex === i ? baseW * CENTER_SCALE : baseW));
  const cardHeights = [0, 1, 2].map((i) => (currentIndex === i ? baseH * CENTER_SCALE : baseH));
  const positions = [
    0,
    cardWidths[0] + TRACK_GAP,
    cardWidths[0] + TRACK_GAP + cardWidths[1] + TRACK_GAP,
  ];
  const trackTranslateX =
    carouselWrapWidth / 2 - positions[currentIndex] - cardWidths[currentIndex] / 2;

  useEffect(() => {
    const updateLayout = () => {
      setWindowWidthPx(window.innerWidth);
      setWindowHeightPx(window.innerHeight);
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  useEffect(() => {
    const wrap = carouselRef.current;
    if (!wrap) return;
    const measure = () => setCarouselWrapWidth(wrap.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [carouselRef]);

  // 仅中间卡片的视频自动播放，其余暂停
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === currentIndex) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [currentIndex]);

  const handleSlideChange = (newIndex: number) => {
    if (isTransitioning) return;
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
    setIsTransitioning(true);
    goToSlide(newIndex);
    transitionTimerRef.current = setTimeout(() => {
      setIsTransitioning(false);
      transitionTimerRef.current = null;
    }, 400);
  };
  handleSlideChangeRef.current = handleSlideChange;

  const handleIndicatorClick = (index: number) => {
    handleSlideChange(index);
  };

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) return;

    const handleGestureStart = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleGestureChange = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleGestureEnd = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDocumentGestureStart = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleDocumentGestureChange = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleDocumentGestureEnd = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const handleWheel = (e: WheelEvent) => {
      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isHorizontalScroll =
        Math.abs(deltaX) > Math.abs(deltaY) ||
        (Math.abs(deltaX) > 0 && Math.abs(deltaY) < 3) ||
        (Math.abs(deltaX) > Math.abs(deltaY) * 0.3 && Math.abs(deltaY) < 10);
      if (isHorizontalScroll && !e.defaultPrevented) {
        e.preventDefault();
      }
    };

    container.addEventListener('gesturestart', handleGestureStart, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gesturechange', handleGestureChange, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gestureend', handleGestureEnd, { passive: false, capture: true });
    container.addEventListener('wheel', handleWheel, { passive: false, capture: false });
    document.addEventListener('gesturestart', handleDocumentGestureStart, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gesturechange', handleDocumentGestureChange, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gestureend', handleDocumentGestureEnd, {
      passive: false,
      capture: true,
    });

    return () => {
      container.removeEventListener('gesturestart', handleGestureStart, { capture: true });
      container.removeEventListener('gesturechange', handleGestureChange, { capture: true });
      container.removeEventListener('gestureend', handleGestureEnd, { capture: true });
      container.removeEventListener('wheel', handleWheel, { capture: false });
      document.removeEventListener('gesturestart', handleDocumentGestureStart, { capture: true });
      document.removeEventListener('gesturechange', handleDocumentGestureChange, { capture: true });
      document.removeEventListener('gestureend', handleDocumentGestureEnd, { capture: true });
    };
  }, [carouselRef]);

  return (
    <section className={styles['screen2-cover']}>
      <div className={styles['screen-top-content']}>
        <div className={styles['screen-top-title']}>真人级的革命性交互</div>
        <div className={styles['screen-top-desc']}>
          自研数字人及语音技术，构造真人级别的AI互动体验
        </div>
      </div>

      <div className={styles['screen2-container']}>
        <div className={styles['screen2-carousel-area']}>
          <div
            className={styles['screen2-carousel-wrap']}
            ref={carouselRef}
            style={{ height: Math.max(...cardHeights) + 2 }}
          >
            <div
              ref={trackRef}
              className={styles['screen2-track']}
              style={{
                transform: `translateX(${trackTranslateX}px)`,
                height: Math.max(...cardHeights) + 2,
              }}
            >
              {[0, 1, 2].map((slotIndex) => {
                const card = CARD_LIST[slotIndex];
                const isCenter = currentIndex === slotIndex;
                return (
                  <div
                    key={slotIndex}
                    role="button"
                    tabIndex={0}
                    className={`${styles['screen2-card']} ${isCenter ? styles['screen2-card-center'] : ''}`}
                    style={{
                      width: cardWidths[slotIndex],
                      height: cardHeights[slotIndex],
                      ...(isCenter ? { marginBottom: 2 } : {}),
                    }}
                    onClick={() => handleSlideChange(slotIndex)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSlideChange(slotIndex);
                      }
                    }}
                  >
                    <div className={styles['screen2-card-left']}>
                      <video
                        ref={(el) => {
                          videoRefs.current[slotIndex] = el;
                        }}
                        src={card.video}
                        className={styles['screen2-card-video']}
                        muted
                        loop
                        playsInline
                        preload="auto"
                      />
                    </div>
                    <div className={styles['screen2-card-right']}>
                      <div className={styles['screen2-card-text']}>
                        <h3 className={styles['screen2-card-title']}>{card.title}</h3>
                        <p className={styles['screen2-card-desc']}>{card.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles['screen2-indicators']}>
            {Array.from({ length: CARD_COUNT }, (_, index) => (
              <div
                key={index}
                className={`${styles['screen2-indicator-hitarea']} ${isTransitioning ? styles['screen2-indicator-hitarea-disabled'] : ''}`}
                onClick={() => handleIndicatorClick(index)}
                onKeyDown={(e) => e.key === 'Enter' && handleIndicatorClick(index)}
                role="button"
                tabIndex={0}
                aria-label={`指示器 ${index + 1}`}
              >
                <span
                  className={`${styles['screen2-indicator']} ${
                    currentIndex === index ? styles['screen2-indicator-active'] : ''
                  } ${isTransitioning ? styles['screen2-indicator-disabled'] : ''}`}
                  aria-hidden
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
